import React, { useRef, useEffect } from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { toggleModal } from 'apps/auth/actions/appAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DetailsItem } from 'apps/auth/components/common/DetailsItem/DetailsItem';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import PaymentPendingModal from 'apps/auth/components/paysendPlus/PaymentPendingModal';
import FeeFreeTransferBenefitsModal from 'apps/auth/components/paysendPlus/plusDashboard/FeeFreeTransferBenefitsModal';
import HowItWorksModal from 'apps/auth/components/paysendPlus/plusDashboard/HowItWorksModal';
import HowItWorksModalForEmployee from 'apps/auth/components/paysendPlus/plusDashboard/HowItWorksModalForEmployee';
import MakingTransfersPopup from 'apps/auth/components/paysendPlus/plusDashboard/MakingTransfersPopup';
import ShowMorePopup from 'apps/auth/components/paysendPlus/plusDashboard/ShowMorePopup';
import SubscriptionPausedPopup from 'apps/auth/components/paysendPlus/plusDashboard/SubscriptionPausedPopup';
import { UnlimitedFeeFreeTransfersCard } from 'apps/auth/components/paysendPlus/plusDashboard/UnlimitedFeeFreeTransfersCard';
import { UnlimitedFeeFreeTransfersCardEmpty } from 'apps/auth/components/paysendPlus/plusDashboard/UnlimitedFeeFreeTransfersCardEmpty';
import { isSubscriptionUpdateRestricted, PAUSED, PAYMENT_PENDING, REPAYMENT_MISSED, SUBSCRIBED, UNSUBSCRIBED } from 'apps/auth/components/paysendPlus/utils';
import BasePage from 'apps/auth/components/utils/BasePage';
import { PLUS_INITIAL_SCREEN_SHOWN, PLUS_SUBSCRIBE_BUTTON_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { AUTH_PLUS_PAYMENT_METHODS_LIST, AUTH_PROFILE, URL_IMAGES_PAYSEND_PLUS, AUTH_PLUS_EMPLOYEE_SUBSCRIBE } from 'apps/auth/constants/UrlConstants';
import { getDayMonthDate, getDDMMMYYYYDate, isDateToday, resolveArguments } from 'apps/auth/helpers/utils';
import { useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import { useMatchMedia } from 'apps/auth/hooks/useMatchMedia';
import breakpoints from 'apps/auth/theme/breakpoints';
import media from 'apps/auth/theme/media';

const DetailsContainer = styled.div`
    width: 50%;
    flex-shrink: 0;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }

    ${media.desktop1920} {
        margin-bottom: 24px;
    }
`;

const PaysendPlusDashboardPage = (props) => {
    const popupRef = useRef(null);
    const {
        plusShowDoesItWork,
        initialTransfersText,
        learnMoreCommon,
        subscribe,
        feeFreeTransfersText,
        plusTransferCount,
        plusPlan,
        plusPaymentMethod,
        plusMoneySaved,
        plusMainTransfers,
        plusNextPayment,
        plusUnpaidPayment,
        plusMakingTransfers,
        plusSubscriptionSince,
        plusSubMenuRenew,
        plusSubMenuHowItWorks,
        plusSubMenuUpdatePaymentMethod,
        plusSubMenuUpdateSubscriptionPlan,
        plusSubMenuCancelSubscription,
        plusSubMenuSupport,
        plusSubMenuFeeFreeTransfers,
        plusSubMenuSendTransfer,
        plusBlocked,
        plusActivated,
        plusPaymentPending,
        plusPlanMonth,
        plusAuthorizeText,
        plusAuthorizeButton,
        plusReactivateText,
        plusReactivateButton,
        plusReauthorizeText,
        plusReauthorizeButton,
        plusPaymentProcessed,
        gotIt,
        plusPaused,
        plusPausedPopupText,
        plusSubscriptionUpdateRestriction,
    } = usePageMessages();
    const {
        dashboard,
        subscriptionCurrencyCode,
        subscriptionPaymentInfo,
        subscriptionInfo,
        plusSubscriptionBlocked,
        plusConfirmSubscription,
        actualPaymentMethod,
        isPaysendEmployee,
    } = useExtra();
    const { lang } = useLanguage();
    const status = subscriptionInfo?.status;
    const isSubscribed = status === SUBSCRIBED;
    const isUnsubscribed = status === UNSUBSCRIBED;
    const isPaymentPending = status === PAYMENT_PENDING;
    const isRepaymentMissed = status === REPAYMENT_MISSED;
    const isPaused = status === PAUSED;
    const isSubscriptionExpired = subscriptionInfo?.addInfo?.is_expired && isUnsubscribed;
    const isCancelled = status === SUBSCRIBED && !subscriptionInfo?.isProlonged;
    const subscriptionEndDate = subscriptionInfo?.endPeriodTs;
    const isPaymentMethodError = subscriptionInfo?.addInfo?.is_payment_method_updated && isSubscribed && isDateToday(subscriptionEndDate);
    const isEmptyDashboard = isSubscriptionExpired || isPaymentPending || !subscriptionInfo;
    const isBlocked = plusSubscriptionBlocked || (isSubscriptionExpired && subscriptionInfo?.isProlonged);
    const isFraudBlock = isDateToday(subscriptionEndDate) && subscriptionInfo?.addInfo?.isFraudUnblocked && status === SUBSCRIBED;
    const isDesktop = useMatchMedia(breakpoints.desktop1224);
    const isUpdateRestricted = isSubscriptionUpdateRestricted(subscriptionEndDate);

    useEffect(() => {
        if (isEmptyDashboard && !isPaymentPending) {
            amplitude.getInstance().logEvent(PLUS_INITIAL_SCREEN_SHOWN);
        }
    }, []);

    const openBenefitsModal = () => {
        props.toggleModal(true, <FeeFreeTransferBenefitsModal />);
    };

    const getActualSubscriptionLink = (isPaysendEmployee = false) => {
        return isPaysendEmployee ? AUTH_PLUS_EMPLOYEE_SUBSCRIBE : AUTH_PLUS_PAYMENT_METHODS_LIST;
    };

    const rightHeaderComponent = (
        <DiskButton
            iconName="dots"
            onClick={() => {
                popupRef.current?.open(
                    <ShowMorePopup
                        popupRef={popupRef}
                        renewSubscription={plusSubMenuRenew}
                        howItWorks={plusSubMenuHowItWorks}
                        updatePaymentMethod={plusSubMenuUpdatePaymentMethod}
                        updateSubscriptionPlan={plusSubMenuUpdateSubscriptionPlan}
                        cancelSubscription={plusSubMenuCancelSubscription}
                        support={plusSubMenuSupport}
                        isSubscriptionExpired={isSubscriptionExpired}
                        isCancelled={isCancelled}
                        subscriptionUpdateRestriction={plusSubscriptionUpdateRestriction}
                        isUpdateRestricted={isUpdateRestricted}
                        gotIt={gotIt}
                    />,
                );
            }}
        />
    );

    const getErrorBlock = () => {
        let errorMessage;
        let buttonText;
        if (isFraudBlock) {
            errorMessage = plusReauthorizeText;
            buttonText = plusReauthorizeButton;
        } else if (isRepaymentMissed) {
            errorMessage = plusReactivateText;
            buttonText = plusReactivateButton;
        } else if (isPaymentMethodError) {
            errorMessage = plusAuthorizeText;
            buttonText = plusAuthorizeButton;
        }
        return (
            <>
                <Text variant="div" color="error" indent="24px">
                    {errorMessage}
                </Text>
                {!isFraudBlock && (
                    <Row indent="24px">
                        <Panel
                            type="link"
                            isHoverable
                            negativeMargin
                            href={isSubscriptionExpired ? getActualSubscriptionLink(isPaysendEmployee) : plusConfirmSubscription}
                        >
                            <DiskBadgeWrapper>
                                <Icon name="arrow-forward" color="primary" />
                            </DiskBadgeWrapper>
                            <Text color="primary">{buttonText}</Text>
                        </Panel>
                    </Row>
                )}
            </>
        );
    };

    return (
        <>
            <CommonPage backUrl={AUTH_PROFILE} rightHeaderComponent={!isEmptyDashboard ? rightHeaderComponent : null}>
                <Row indent="adaptive-24-32" textAlign={isEmptyDashboard ? 'center' : null}>
                    <img src={`${URL_IMAGES_PAYSEND_PLUS}/paysend-plus-logo.svg`} alt="plus-logo" width="316" />
                </Row>

                {isEmptyDashboard && (
                    <>
                        <Text variant="div" textAlign="center">
                            {feeFreeTransfersText}
                        </Text>
                        <Button
                            variant="row"
                            caption={plusShowDoesItWork}
                            textCenter
                            onClick={() => props.toggleModal(true, isPaysendEmployee ? <HowItWorksModalForEmployee /> : <HowItWorksModal />)}
                        />
                        {!isDesktop && (
                            <Row indent="48px" textAlign="center">
                                <Button
                                    type="link"
                                    href={!isPaymentPending ? getActualSubscriptionLink(isPaysendEmployee) : null}
                                    caption={isPaymentPending ? plusPaymentPending : subscribe}
                                    onClick={() => {
                                        amplitude.getInstance().logEvent(PLUS_SUBSCRIBE_BUTTON_CLICKED);
                                        isPaymentPending ? props.toggleModal(true, <PaymentPendingModal gotIt={gotIt} text={plusPaymentProcessed} />) : null;
                                    }}
                                />
                            </Row>
                        )}
                    </>
                )}
                {(isRepaymentMissed || plusSubscriptionBlocked || isPaymentMethodError) && <>{getErrorBlock()}</>}
                {!isPaysendEmployee && !isEmptyDashboard && (
                    <Row indent="adaptive-48-64" flex flexwrap>
                        <DetailsContainer>
                            {!isPaused && isRepaymentMissed && <DetailsItem detailsTitle={plusNextPayment} detailsText={plusUnpaidPayment} />}
                            {isPaymentMethodError && !isPaused && !isRepaymentMissed && (
                                <DetailsItem detailsTitle={plusNextPayment} detailsText={getDayMonthDate(subscriptionInfo?.endPeriodTs, lang, 'short')} />
                            )}
                            {!isPaymentMethodError && !isPaused && !isRepaymentMissed && (
                                <DetailsItem
                                    detailsTitle={plusNextPayment}
                                    detailsText={getDayMonthDate(subscriptionPaymentInfo?.extra?.plus__payment_next_date, lang, 'short')}
                                />
                            )}
                            {isPaused && (
                                <>
                                    <Text variant="div" color="secondary" size="secondary" indent="4px">
                                        {plusNextPayment}
                                    </Text>
                                    <Text color="primary">{plusPaused}</Text>
                                    <DiskButton
                                        iconName="info-rounded"
                                        size="small"
                                        raised={false}
                                        iconColor="primary"
                                        type="button"
                                        onClick={() =>
                                            popupRef.current?.open(<SubscriptionPausedPopup popupRef={popupRef} text={plusPausedPopupText} gotIt={gotIt} />)
                                        }
                                    />
                                </>
                            )}
                        </DetailsContainer>
                        <DetailsContainer>
                            <DetailsItem detailsTitle={plusPlan} detailsText={`${subscriptionInfo?.amount} ${subscriptionCurrencyCode}/${plusPlanMonth}`} />
                        </DetailsContainer>
                        <DetailsContainer>
                            <DetailsItem detailsTitle={plusPaymentMethod} detailsText={actualPaymentMethod} />
                        </DetailsContainer>
                    </Row>
                )}

                {!isEmptyDashboard && (
                    <Text variant="div" size="subheader" color="secondary" indent="32px">
                        {plusMakingTransfers}
                    </Text>
                )}
                <Row indent={isEmptyDashboard ? 'adaptive-24-32' : 'adaptive-48-64'}>
                    {isEmptyDashboard ? (
                        <UnlimitedFeeFreeTransfersCardEmpty
                            openModal={openBenefitsModal}
                            learnMore={learnMoreCommon}
                            title={plusMainTransfers}
                            text={initialTransfersText}
                        />
                    ) : (
                        <UnlimitedFeeFreeTransfersCard
                            plusTransferCount={plusTransferCount}
                            plusMoneySaved={plusMoneySaved}
                            plusMainTransfers={plusMainTransfers}
                            dashboard={dashboard}
                            subscriptionCurrencyCode={subscriptionCurrencyCode}
                            subscriptionStartDate={resolveArguments(plusSubscriptionSince, getDDMMMYYYYDate(dashboard?.startOfPeriod, lang))}
                            subscriptionStatus={isBlocked ? plusBlocked : plusActivated}
                            isBlocked={isBlocked}
                            onCardClick={() => {
                                popupRef.current?.open(
                                    <MakingTransfersPopup
                                        popupRef={popupRef}
                                        feeFreeTransfers={plusSubMenuFeeFreeTransfers}
                                        sendTransfer={plusSubMenuSendTransfer}
                                        support={plusSubMenuSupport}
                                    />,
                                );
                            }}
                        />
                    )}
                </Row>
                {isEmptyDashboard && isDesktop && (
                    <FixedBottomPanel isRightAligned>
                        <Button
                            type="link"
                            href={!isPaymentPending ? getActualSubscriptionLink(isPaysendEmployee) : null}
                            caption={isPaymentPending ? plusPaymentPending : subscribe}
                            onClick={() => {
                                amplitude.getInstance().logEvent(PLUS_SUBSCRIBE_BUTTON_CLICKED);
                                isPaymentPending ? props.toggleModal(true, <PaymentPendingModal gotIt={gotIt} text={plusPaymentProcessed} />) : null;
                            }}
                        />
                    </FixedBottomPanel>
                )}
            </CommonPage>

            <PopupNew ref={popupRef} />
        </>
    );
};

export default connect(null, { toggleModal })(BasePage(PaysendPlusDashboardPage));
